import { useCallback, useMemo } from 'react'
import { useIntl, FormattedMessage } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import moment from 'moment'

// LuxKit Components
import BodyText from 'LuxKit/Typography/BodyText'
import Group from 'components/utils/Group'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import CardCarousel from 'LuxKit/Carousel/CardCarousel'
import Caption from 'LuxKit/Typography/Caption'
import FormatCurrency from 'components/Common/FormatCurrency'
import Places from 'constants/places'
import Clickable from 'components/Common/Clickable'
import focusStyle from 'styles/utils/focusStyle'
import { mediaHoverable } from 'lib/theme/mediaQueries'
import clsx from 'clsx'
import { useAppSelector } from 'hooks/reduxHooks'
import getHasLoyaltyAccount from 'loyalty/selectors/getHasLoyaltyAccount'
import getOfferListKey from 'lib/offer/offerListKey'
import { buildBundleOfferFilters } from '../MaldivesDirectUtils'
import { HOTEL_OFFERS_LIMIT, MALDIVES_BUNDLE_DEFAULT_NIGHTS } from 'constants/charterFlight'
import { sumUpOccupancies } from 'lib/offer/occupancyUtils'
import { FlightsClassTypes, type FareClassWithReward } from 'constants/flight'
import type { CalendarItemBundleData } from '../hooks/types'
import type { MaldivesCabinHotelEntry } from '../hooks/useMaldivesOffersPricing'
import config from 'constants/config'
import { LUXURY_ESCAPES } from 'constants/brands'
import { getLowestAvailableHotelPrice, useSeatEligiblePassengers } from '../hooks/utils'
import useAllCharterFlightJourneys from 'hooks/CharterFlights/useAllCharterFlightJourneys'
import useCharterRewardSeatsIndicatorForDate from '../hooks/useCharterRewardSeatsIndicatorForDate'
import useFeatureFlag from 'hooks/Optimizely/useFeatureFlag'
import { OptimizelyFeatureFlags } from 'constants/optimizely'
import {
  CharterRewardAvailabilityContext,
  type CabinDateJourneys,
} from '../hooks/charterRewardAvailabilityContext'
import { CHARTER_CASH_CABINS } from 'lib/maldivesCharter/charterCabins'
import { aggregateMaldivesCharterMonthTileRewardPricing } from './maldivesCharterMonthTileRewardPricing'
import type { CharterRewardLeg } from 'hooks/Flights/useCharterRewardAvailability'
import { MALDIVES_CHARTER_ROUTE } from 'api/lib/flightDealMap'
import ClassicRewardsSealIcon from 'LuxKit/Icons/loyalty/ClassicRewardsSealIcon'
import SolidFireIcon from 'LuxKit/Icons/solid/SolidFireIcon'
import FormatLoyaltyPoints from 'loyalty/components/Formatters/FormatLoyaltyPoints'
import { charterCalendarDayKey } from 'lib/maldivesCharter/charterCalendarDayKey'
import { REWARD_PREVIEW_RATE_BY_CABIN } from 'lib/maldivesCharter/classicRewardPricing'
import type { DateItem } from './MaldivesTripOptionsList'
import MaldivesCharterTripOptionsTable from './MaldivesCharterTripOptionsTable'
import { trimWhere } from 'lib/array/arrayUtils'
import TextLink from 'LuxKit/TextLink'
import { buildSearchParamsForFilterPane } from 'components/Pages/DestinationLandingPage/DestinationLandingPageUtils'
import { INTL_SHORT_MONTH } from 'constants/dateFormats'
import { useIntlDateFormatter } from 'lib/datetime/dateUtils'

// Types
interface DateRange {
  departureDate: string
  returnDate: string
  checkIn: string
  checkOut: string
  durationDays: number // Total days from departureDate to returnDate
  nights: number // Nights from checkIn to checkOut
}

interface TripOption {
  id: string
  departDate: string
  returnDate: string
  checkIn: string
  checkOut: string
  price: number
  memberPrice?: number
  currency: string
  seatsLeft?: number
  nights: number
  available: boolean
  month?: string
  fareClass?: FareClassWithReward
}

const ScrollableContainer = styled(VerticalSpacer)`
  width: 100%;
  min-width: 0;
`

// Styled Components - Improved Month Selector (inspired by TourV2)
const MonthTile = styled(Clickable)`
  position: relative;
  padding: ${rem(8)} ${rem(12)};
  text-align: center;
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  min-height: ${rem(110)};
  min-width: ${rem(104)};
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  transition: border-color 0.2s, background-color 0.2s;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${rem(2)};

  &:focus-visible {
    ${focusStyle}
  }

  ${mediaHoverable} {
    &:hover {
      background-color: ${props => props.theme.palette.neutral.surface.lightBackground};
    }
  }

  &.selected {
    border: 1px solid ${props => props.theme.palette.neutral.surface.darkForeground};
  }
`

const StyledCardCarousel = styled(CardCarousel)`
  margin-top: ${rem(16)};
`

const TripOptionsContainer = styled(Group)`
  width: 100%;
  margin-top: ${rem(20)};
`

const CalendarContainer = styled(VerticalSpacer)`
  background-color: ${props => props.theme.palette.neutral.surface.lightBackground};
  padding: ${rem(20)} ${rem(20)} ${rem(8)};
  margin: 0 ${rem(-24)} ${rem(-24)};
`

const BottomLinkContainer = styled.div`
  padding: ${rem(20)};
  display: flex;
  justify-content: center;
`

interface Props {
  onDateSelect: (option: TripOption) => void
  selectedOptionId?: string
  nights?: number
  occupancies?: Array<App.Occupants>
  fareClass: FlightsClassTypes
  /** Full selected fare class including reward variant — used for the "Selected" indicator in the trip table. */
  selectedFareClass?: FareClassWithReward
  priceMode: 'total' | 'perPerson'
  // LuxPlus related
  luxPlusEnabled?: boolean
  dateRangesByMonth: Record<string, Array<DateRange>>
  selectedMonth: string
  onMonthChange: (month: string) => void
  /** Ready-to-use calendar item data - no calculation needed **/
  itemBundleDataByDate?: Record<string, CalendarItemBundleData>
  /** Per-cabin lowest hotel by date so each per-cabin cell in the trip table can use
   *  the hotel min for offers that allow that cabin (independent of page selection). */
  hotelPriceByCabinAndDate?: Partial<Record<FlightsClassTypes, Record<string, MaldivesCabinHotelEntry>>>
  mode: 'search' | 'offer'
  /** When mode is 'offer', pass the offer to show Kids Fly Free label only when offer has campaign tag */
  offer?: App.HotelOffer
  /** When true, hide dates and trip-table rows that don't have reward-seat availability for every leg. */
  rewardSeatsOnly?: boolean
  /**
   * Optional cabin filter from the modal's class dropdown. When set, the trip-options table
   * narrows visible columns to that base cabin's cash + reward variants.
   */
  cabinFilter?: FlightsClassTypes | null
}

function MaldivesDirectSearchFlightCalendar(props: Props) {
  const {
    onDateSelect,
    selectedOptionId,
    nights = MALDIVES_BUNDLE_DEFAULT_NIGHTS,
    occupancies,
    fareClass,
    priceMode,
    luxPlusEnabled: luxPlusEnabledProp,
    dateRangesByMonth,
    selectedMonth,
    onMonthChange,
    mode,
    itemBundleDataByDate,
    hotelPriceByCabinAndDate,
    rewardSeatsOnly = false,
  } = props

  const intl = useIntl()
  const dateFormatter = useIntlDateFormatter()

  const luxPlusEnabled = Boolean(luxPlusEnabledProp && config.BRAND === LUXURY_ESCAPES)

  const allOfferLists = useAppSelector(state => state.offer.offerLists)
  const allOffers = useAppSelector(state => state.offer.offers)
  const allOfferMetaData = useAppSelector(state => state.offer.searchResultMetadata.offerMetaData)

  // Calculate total passengers for bundle pricing
  const totalPassengers = useMemo(
    () => sumUpOccupancies(occupancies),
    [occupancies],
  )
  const seatEligiblePassengers = useSeatEligiblePassengers(occupancies ?? [{ adults: 2, children: 0 }])

  const effectiveOccupancies = occupancies ?? [{ adults: 2, children: 0 }]

  // Fetch one journey schedule per cash cabin so each cash column shows its own price.
  // Calls are cached at the Redux level (per cabin's flightSearchKey), so calling N times
  // with the same args is fine — the second mount just reads from the existing slice.
  // CHARTER_CASH_CABINS is a stable 3-tuple [ECONOMY, PREMIUM, BUSINESS]; explicit calls
  // keep us within the rules of hooks.
  const economyJourneys = useAllCharterFlightJourneys({
    charterBundleRoute: MALDIVES_CHARTER_ROUTE,
    occupancies: effectiveOccupancies,
    fareClass: FlightsClassTypes.ECONOMY,
  })
  const premiumJourneys = useAllCharterFlightJourneys({
    charterBundleRoute: MALDIVES_CHARTER_ROUTE,
    occupancies: effectiveOccupancies,
    fareClass: FlightsClassTypes.PREMIUM,
  })
  const businessJourneys = useAllCharterFlightJourneys({
    charterBundleRoute: MALDIVES_CHARTER_ROUTE,
    occupancies: effectiveOccupancies,
    fareClass: FlightsClassTypes.BUSINESS,
  })

  // Per-cabin per-date journey lookup. Date key is `checkIn` (matches outbound arrival;
  // inbound departure matches `checkOut` for the same row).
  const journeysByCabinDate = useMemo(() => {
    const map = new Map<FlightsClassTypes, Map<string, CabinDateJourneys>>()
    const cabinFares: Array<[FlightsClassTypes, ReturnType<typeof useAllCharterFlightJourneys>]> = [
      [FlightsClassTypes.ECONOMY, economyJourneys],
      [FlightsClassTypes.PREMIUM, premiumJourneys],
      [FlightsClassTypes.BUSINESS, businessJourneys],
    ]
    cabinFares.forEach(([cabin, fares]) => {
      const cabinMap = new Map<string, CabinDateJourneys>()
      const departingJourneys = fares?.fares?.[0] ?? []
      const returningJourneys = fares?.fares?.[1] ?? []
      Object.values(dateRangesByMonth).forEach((dateRanges) => {
        dateRanges.forEach(({ checkIn, checkOut }) => {
          const departing = departingJourneys.find((journey) => {
            const arrival = charterCalendarDayKey(journey.flightGroup.flights.at(-1)?.arrivalDate)
            return arrival === checkIn
          })
          const returning = returningJourneys.find((journey) => {
            const departure = charterCalendarDayKey(journey.flightGroup.flights.at(0)?.departingDate)
            return departure === checkOut
          })
          cabinMap.set(checkIn, { departing, returning })
        })
      })
      map.set(cabin, cabinMap)
    })
    return map
  }, [economyJourneys, premiumJourneys, businessJourneys, dateRangesByMonth])

  const getJourneysForCabinDate = useCallback(
    (cabin: FlightsClassTypes, dateKey: string): CabinDateJourneys | undefined => {
      return journeysByCabinDate.get(cabin)?.get(dateKey)
    },
    [journeysByCabinDate],
  )

  const { rewardLegs, rewardLegsByDate } = useMemo(() => {
    // Reward indicator coverage: include every cabin's journey_keys in the flat `legs`
    // array (deduped) so per-cell `getLegResult(journeyKey)` works for any cabin's leg.
    // `legsByDate` is intentionally kept to the page's selected cabin only — the existing
    // per-date all-or-nothing `isAvailableForDate` semantics drive the month-tile seal,
    // and broadening to "any cabin available" would change that meaning.
    const legs: Array<CharterRewardLeg> = []
    const legsByDate: Record<string, Array<string>> = {}
    const seenJourneyKeys = new Set<string>()

    const pageCabin = CHARTER_CASH_CABINS.includes(fareClass) ? fareClass : FlightsClassTypes.ECONOMY
    const pageCabinMap = journeysByCabinDate.get(pageCabin)

    Object.values(dateRangesByMonth).forEach((dateRanges) => {
      dateRanges.forEach(({ checkIn }) => {
        const pageEntry = pageCabinMap?.get(checkIn)
        if (pageEntry?.departing?.journeyKey && pageEntry.returning?.journeyKey) {
          legsByDate[checkIn] = [pageEntry.departing.journeyKey, pageEntry.returning.journeyKey]
        }

        // Flatten across all cabins for the indicator's leg coverage.
        CHARTER_CASH_CABINS.forEach((cabin) => {
          const entry = journeysByCabinDate.get(cabin)?.get(checkIn)
          for (const journey of [entry?.departing, entry?.returning]) {
            const key = journey?.journeyKey
            if (!key || seenJourneyKeys.has(key)) continue
            seenJourneyKeys.add(key)
            legs.push({ journeyKey: key, legTotal: journey.price.all.totalFare ?? 0 })
          }
        })
      })
    })

    return { rewardLegs: legs, rewardLegsByDate: legsByDate }
  }, [journeysByCabinDate, dateRangesByMonth, fareClass])

  const isRewardSeatsEnabled = useFeatureFlag(OptimizelyFeatureFlags.maldivesCharterRewardSeats)
  const memberId = useAppSelector(state => state.auth.account.memberId)
  const hasLoyaltyAccount = useAppSelector(getHasLoyaltyAccount)

  const rewardIndicator = useCharterRewardSeatsIndicatorForDate({
    legs: rewardLegs,
    legsByDate: rewardLegsByDate,
    passengerCount: seatEligiblePassengers,
    disabled: !isRewardSeatsEnabled || !hasLoyaltyAccount || rewardLegs.length === 0,
  })

  const charterRewardContextValue = useMemo(() => ({
    ...rewardIndicator,
    getJourneysForCabinDate,
  }), [rewardIndicator, getJourneysForCabinDate])

  // The cabin used for month-tile flight pricing: cabinFilter (when a cabin dropdown is
  // selected) falls back to the page-level fareClass.
  const cabinFilter = props.cabinFilter ?? null
  const monthTileFareClass = cabinFilter ?? fareClass
  const rewardLockedForLogin = rewardSeatsOnly && (!memberId || !hasLoyaltyAccount)

  // Hardcoded preview rates for logged-out / non-member users: cheapest cabin rate + cheapest hotel per month
  const rewardPreviewByMonth = useMemo(() => {
    if (!rewardLockedForLogin) return {}
    const cabinsToCheck = cabinFilter ? [cabinFilter] : [...CHARTER_CASH_CABINS]
    // Find cheapest cabin rate
    let cheapestRate: { pointsPerPerson: number; cashPerPerson: number } | undefined
    for (const cabin of cabinsToCheck) {
      const rate = REWARD_PREVIEW_RATE_BY_CABIN[cabin]
      if (rate && (!cheapestRate || rate.cashPerPerson < cheapestRate.cashPerPerson)) {
        cheapestRate = rate
      }
    }
    if (!cheapestRate) return {}
    const flightPoints = cheapestRate.pointsPerPerson * seatEligiblePassengers
    const flightCash = cheapestRate.cashPerPerson * seatEligiblePassengers

    const result: Record<string, { points: number; cash: number }> = {}
    Object.entries(dateRangesByMonth).forEach(([monthName, dateRanges]) => {
      let cheapestHotel: number | undefined
      dateRanges.forEach(({ checkIn }) => {
        // Try hotelPriceByCabinAndDate for each visible cabin
        for (const cabin of cabinsToCheck) {
          const h = hotelPriceByCabinAndDate?.[cabin]?.[checkIn]
          const hp = h ? (luxPlusEnabled ? (h.hotelMemberPrice || h.hotelPrice || 0) : (h.hotelPrice || 0)) : 0
          if (hp > 0 && (cheapestHotel === undefined || hp < cheapestHotel)) {
            cheapestHotel = hp
          }
        }
        // Fallback to itemBundleDataByDate
        if (cheapestHotel === undefined) {
          const itemData = itemBundleDataByDate?.[checkIn]
          if (itemData?.available && itemData.hotelPrice && itemData.hotelPrice > 0) {
            if (cheapestHotel === undefined || itemData.hotelPrice < cheapestHotel) {
              cheapestHotel = itemData.hotelPrice
            }
          }
        }
      })
      if (cheapestHotel !== undefined) {
        result[monthName] = { points: flightPoints, cash: cheapestHotel + flightCash }
      }
    })
    return result
  }, [rewardLockedForLogin, cabinFilter, seatEligiblePassengers, dateRangesByMonth, hotelPriceByCabinAndDate, itemBundleDataByDate, luxPlusEnabled])

  const monthRewardSeals = useMemo(() => {
    // When a cabinFilter is active, check only that cabin's reward availability.
    // Otherwise check ANY cabin so users discover reward inventory before picking a fare class.
    const cabinsToCheck = cabinFilter ? [cabinFilter] : [...CHARTER_CASH_CABINS]
    const datesByMonth: Record<string, Array<{ dateKey: string; allLegsAvailable: boolean }>> = {}
    Object.entries(dateRangesByMonth).forEach(([monthName, dateRanges]) => {
      datesByMonth[monthName] = dateRanges.map(({ checkIn }) => {
        const anyCabinAvailable = cabinsToCheck.some((cabin) => {
          const entry = journeysByCabinDate.get(cabin)?.get(checkIn)
          const depKey = entry?.departing?.journeyKey
          const retKey = entry?.returning?.journeyKey
          if (!depKey || !retKey) return false
          return rewardIndicator.getLegResult(depKey)?.available === true
            && rewardIndicator.getLegResult(retKey)?.available === true
        })
        return { dateKey: checkIn, allLegsAvailable: anyCabinAvailable }
      })
    })
    return aggregateMaldivesCharterMonthTileRewardPricing({ datesByMonth })
  }, [dateRangesByMonth, journeysByCabinDate, rewardIndicator, cabinFilter])

  // Generate months for the selector with bundle pricing
  const months = useMemo(() => {
    const monthEntries: Array<{
      name: string
      price: number
      /** Lowest reward points for the month (total across all passengers). */
      rewardPoints?: number
      /** Lowest reward cash co-pay for the month (total). */
      rewardCash?: number
      fetching: boolean
      monthLabel: string
      year: number
      unavailable: boolean
      hasPromotion: boolean
    }> = []

    Object.entries(dateRangesByMonth).forEach(([monthName, dateRanges]) => {
      // Skip months with no date entries
      if (dateRanges.length === 0) return

      // Calculate lowest bundle price per person for this month
      let lowestPrice = 0
      let lowestRewardPoints: number | undefined
      let lowestRewardCash: number | undefined
      let allResolved = true
      let hasPromotion = false

      dateRanges.forEach(({ checkIn, checkOut }) => {
        const hasPromotionForDate = CHARTER_CASH_CABINS.some((cabin) => {
          const entry = journeysByCabinDate.get(cabin)?.get(checkIn)
          return !!(entry?.departing?.promotionPrice || entry?.returning?.promotionPrice)
        })
        hasPromotion = hasPromotion || hasPromotionForDate

        // When a cabinFilter is active in search mode, compute per-cabin pricing from
        // journeysByCabinDate + hotelPriceByCabinAndDate. In offer mode, itemBundleDataByDate
        // already reflects the correct pricing so always prefer it when available.
        if (itemBundleDataByDate && (mode === 'offer' || !cabinFilter)) {
          const itemData = itemBundleDataByDate[checkIn]

          // Consider unresolved if item is still fetching
          if (itemData?.fetching) {
            allResolved = false
          }

          // Calculate bundle price based on priceMode and luxPlusEnabled
          const bundlePrice = priceMode === 'total'
            ? (luxPlusEnabled ? (itemData?.memberTotalPrice || itemData?.totalPrice) : itemData?.totalPrice)
            : (luxPlusEnabled ? (itemData?.memberPricePerPerson || itemData?.pricePerPerson) : itemData?.pricePerPerson)

          if (itemData?.available && bundlePrice && bundlePrice > 0) {
            if (lowestPrice === 0 || bundlePrice < lowestPrice) {
              lowestPrice = bundlePrice
            }
          }

          // Reward pricing — scan all cabins for the cheapest reward bundle for this date.
          // Use hotel-only price (not the full bundle which includes the cash flight fare).
          if (rewardSeatsOnly) {
            const cabinsToScan = cabinFilter ? [cabinFilter] : [...CHARTER_CASH_CABINS]
            for (const cabin of cabinsToScan) {
              const entry = journeysByCabinDate.get(cabin)?.get(checkIn)
              if (!entry?.departing?.journeyKey || !entry?.returning?.journeyKey) continue
              const depResult = rewardIndicator.getLegResult(entry.departing.journeyKey)
              const retResult = rewardIndicator.getLegResult(entry.returning.journeyKey)
              if (!depResult?.available || !retResult?.available) continue
              if (depResult.pointsPrice == null || retResult.pointsPrice == null) continue
              if (depResult.cashPrice == null || retResult.cashPrice == null) continue
              // Hotel price from per-cabin data, or derive from bundle by subtracting the cabin's flight fare
              const cabinHotel = hotelPriceByCabinAndDate?.[cabin]?.[checkIn]
              let hotelForReward = 0
              if (cabinHotel) {
                hotelForReward = luxPlusEnabled
                  ? (cabinHotel.hotelMemberPrice || cabinHotel.hotelPrice || 0)
                  : (cabinHotel.hotelPrice || 0)
              } else if (itemData?.available) {
                const cabinFlightFare = (entry.departing.price?.all?.totalFare ?? 0) + (entry.returning.price?.all?.totalFare ?? 0)
                const bundleTotal = luxPlusEnabled ? (itemData.memberTotalPrice || itemData.totalPrice || 0) : (itemData.totalPrice || 0)
                hotelForReward = Math.max(0, bundleTotal - cabinFlightFare)
              }
              if (!hotelForReward) continue
              const totalPoints = (depResult.pointsPrice + retResult.pointsPrice) * seatEligiblePassengers
              const totalCash = (depResult.cashPrice + retResult.cashPrice) * seatEligiblePassengers + hotelForReward
              const rewardTotal = priceMode === 'perPerson' && totalPassengers > 0 ? totalCash / totalPassengers : totalCash
              const rewardPts = priceMode === 'perPerson' && totalPassengers > 0 ? totalPoints / totalPassengers : totalPoints
              if (lowestRewardCash === undefined || rewardTotal < lowestRewardCash) {
                lowestRewardCash = rewardTotal
                lowestRewardPoints = rewardPts
              }
            }
          }
        } else if (mode === 'search' || cabinFilter) {
          // Per-cabin pricing: use the cabin's flight journeys + per-cabin hotel price
          const cabinEntry = journeysByCabinDate.get(monthTileFareClass)?.get(checkIn)
          const depFare = cabinEntry?.departing?.price?.all?.totalFare ?? 0
          const retFare = cabinEntry?.returning?.price?.all?.totalFare ?? 0
          const totalFlightFare = depFare + retFare

          // Hotel price — prefer per-cabin data when available
          const cabinHotel = hotelPriceByCabinAndDate?.[monthTileFareClass]?.[checkIn]
          let hotelPrice = 0
          if (cabinHotel) {
            hotelPrice = luxPlusEnabled
              ? (cabinHotel.hotelMemberPrice || cabinHotel.hotelPrice || 0)
              : (cabinHotel.hotelPrice || 0)
          } else if (!cabinFilter) {
            // Fallback to Redux offer list for non-filtered search mode
            const hotelFilters: App.OfferListFilters = buildBundleOfferFilters({
              destinationId: Places.Maldives.id,
              destinationName: Places.Maldives.name,
              checkIn,
              checkOut,
              rooms: occupancies || [{ adults: 2, children: 0 }],
              limit: HOTEL_OFFERS_LIMIT,
            })
            const hotelKey = getOfferListKey(hotelFilters)
            const offerList = allOfferLists[hotelKey]
            const offerMetaDataForList = allOfferMetaData[hotelKey]
            const offerIds = offerList?.offerIds || []
            hotelPrice = getLowestAvailableHotelPrice({
              offerIds,
              allOffers,
              offerMetaData: offerMetaDataForList,
              rooms: occupancies || [{ adults: 2, children: 0 }],
              nights,
              luxPlusEnabled,
            })
            const offerListResolved = !!offerList && !offerList.fetching && !!offerMetaDataForList
            if (!offerListResolved) allResolved = false
          }

          const flightResolved = totalFlightFare > 0
          const hotelResolved = hotelPrice > 0
          // When using pre-fetched journeysByCabinDate (cabinFilter path), missing
          // flights mean the date is unavailable, not still loading. Only mark
          // unresolved in the Redux-fallback search path.
          if (!flightResolved && !cabinFilter) allResolved = false

          if (flightResolved && hotelResolved) {
            const bundleTotal = hotelPrice + totalFlightFare
            const bundlePrice = priceMode === 'perPerson' && totalPassengers > 0
              ? bundleTotal / totalPassengers
              : bundleTotal

            if (bundlePrice > 0 && (lowestPrice === 0 || bundlePrice < lowestPrice)) {
              lowestPrice = bundlePrice
            }
          }

          // Reward pricing for this date — accumulate cheapest across month
          if (rewardSeatsOnly && cabinEntry?.departing?.journeyKey && cabinEntry?.returning?.journeyKey) {
            const depResult = rewardIndicator.getLegResult(cabinEntry.departing.journeyKey)
            const retResult = rewardIndicator.getLegResult(cabinEntry.returning.journeyKey)
            if (depResult?.available && retResult?.available
              && depResult.pointsPrice != null && retResult.pointsPrice != null
              && depResult.cashPrice != null && retResult.cashPrice != null) {
              const totalPoints = (depResult.pointsPrice + retResult.pointsPrice) * seatEligiblePassengers
              const totalCash = (depResult.cashPrice + retResult.cashPrice) * seatEligiblePassengers + hotelPrice
              const rewardTotal = priceMode === 'perPerson' && totalPassengers > 0 ? totalCash / totalPassengers : totalCash
              const rewardPts = priceMode === 'perPerson' && totalPassengers > 0 ? totalPoints / totalPassengers : totalPoints
              if (lowestRewardCash === undefined || rewardTotal < lowestRewardCash) {
                lowestRewardCash = rewardTotal
                lowestRewardPoints = rewardPts
              }
            }
          }
        }
      })

      // Extract month and year for display
      const firstDate = dateRanges[0]?.checkIn
      const date = moment(firstDate)

      monthEntries.push({
        name: monthName,
        price: lowestPrice || 0,
        rewardPoints: lowestRewardPoints,
        rewardCash: lowestRewardCash,
        fetching: !allResolved,
        monthLabel: dateFormatter(date, INTL_SHORT_MONTH),
        year: date.year(),
        unavailable: allResolved && !lowestPrice,
        hasPromotion,
      })
    })

    return trimWhere(monthEntries, m => m.unavailable)
  }, [dateFormatter, dateRangesByMonth, nights, luxPlusEnabled, totalPassengers, seatEligiblePassengers, allOfferLists, allOffers, allOfferMetaData, occupancies, priceMode, mode, itemBundleDataByDate, cabinFilter, monthTileFareClass, journeysByCabinDate, hotelPriceByCabinAndDate, rewardSeatsOnly, rewardIndicator])

  // Generate all date items for ALL months (lightweight - just dates)
  const allDateItems = useMemo(() => {
    const items: Array<DateItem> = []

    // Loop through all months
    Object.entries(dateRangesByMonth).forEach(([monthName, dateRanges]) => {
      // Skip months with no date entries
      if (dateRanges.length === 0) return

      dateRanges.forEach(({ departureDate, returnDate, checkIn, checkOut }) => {
        items.push({
          id: `${checkIn}-${nights}n`,
          checkIn: checkIn,
          checkOut: checkOut,
          departDate: departureDate,
          returnDate: returnDate,
          month: monthName,
        })
      })
    })

    return items
  }, [dateRangesByMonth, nights])

  const handleMonthSelect = useCallback((month: string) => {
    onMonthChange(month)
  }, [onMonthChange])

  const handleOptionSelect = useCallback((option: TripOption) => {
    onDateSelect(option)
  }, [onDateSelect])

  // Render month carousel content (reusable)
  const monthCarousel = (
    <StyledCardCarousel
      gap={12}
      alignment="center"
      snap="start"
      gutterSize={0}
      gutterStyle="overflow"
    >
      {months.map((month) => {
        const priceColour = luxPlusEnabled ? 'lux-plus' : undefined
        const showRewardSeal = monthRewardSeals[month.name]?.showRewardSeal ?? false
        const showPromotionIcon = month.hasPromotion && !month.fetching
        const showRewardPrice = rewardSeatsOnly && month.rewardPoints != null && month.rewardCash != null
        return (
          <MonthTile
            key={month.name}
            onClick={() => handleMonthSelect(month.name)}
            className={clsx({
              selected: selectedMonth === month.name,
            })}
          >
            <BodyText variant="small" colour="light-foreground-alt">
              {month.year}
            </BodyText>
            <BodyText variant="large">{month.monthLabel}</BodyText>
            {month.fetching && <Caption variant="small">
              {intl.formatMessage({
                id: 'ahqwZJxNJlNqe4lst3xR',
                defaultMessage: 'Loading...',
                description: 'Loading state text for charter flight month price in the flights section',
              })}
            </Caption>}
            {!month.fetching && showRewardPrice && <>
              <Caption variant="medium" weight="bold" colour={priceColour}>
                <FormatLoyaltyPoints points={month.rewardPoints!} abbreviated />
              </Caption>
              <Caption variant="small" colour={priceColour ?? 'neutral-two'}>
                <FormattedMessage
                  id="nJ9gIfPenFxdQlMiiP0K"
                  defaultMessage="+ {cash}"
                  description="Cash co-pay add-on shown below the points amount on a month tile when Société Rewards is active"
                  values={{ cash: <FormatCurrency value={month.rewardCash!} format="roundedDollar" /> }}
                />
              </Caption>
            </>}
            {!month.fetching && rewardLockedForLogin && rewardPreviewByMonth[month.name] && <>
              <Caption variant="medium" weight="bold" colour={priceColour}>
                <FormatLoyaltyPoints points={rewardPreviewByMonth[month.name].points} abbreviated />
              </Caption>
              <Caption variant="small" colour={priceColour ?? 'neutral-two'}>
                <FormattedMessage
                  id="nJ9gIfPenFxdQlMiiP0K"
                  defaultMessage="+ {cash}"
                  description="Cash co-pay add-on shown below the points amount on a month tile when Société Rewards is active"
                  values={{ cash: <FormatCurrency value={rewardPreviewByMonth[month.name].cash} format="roundedDollar" /> }}
                />
              </Caption>
            </>}
            {!month.fetching && rewardSeatsOnly && !rewardLockedForLogin && !showRewardPrice && <Caption variant="small" colour="neutral-three">
              {intl.formatMessage({
                id: '_6M4zK3l3gZY9mwCY7aN',
                defaultMessage: 'No seats available',
                description: 'Text shown on month tile when Société Rewards is on but no reward seats exist for that month',
              })}
            </Caption>}
            {!month.fetching && !rewardSeatsOnly && <BodyText variant="large" weight="bold" colour={priceColour}>
              {month.price > 0 && <FormatCurrency value={month.price} format="roundedDollar" />}
              {!month.price && <Caption variant="small">
                {intl.formatMessage({
                  id: 'OlOfqYtNbU_4z3O0cdFc',
                  defaultMessage: 'Unavailable',
                  description: 'Text shown when no charter flights available for month',
                })}
              </Caption>}
            </BodyText>}
            <Group direction="horizontal" horizontalAlign="center" gap={2}>
              {isRewardSeatsEnabled && (showRewardSeal || !hasLoyaltyAccount) && !month.fetching &&
                <ClassicRewardsSealIcon size={20} colour="highlight-primary-dark" aria-hidden />}
              {showPromotionIcon && !month.fetching && <SolidFireIcon size="S" colour="urgency" aria-hidden />}
            </Group>
          </MonthTile>
        )
      })}
    </StyledCardCarousel>
  )

  return (
    <CharterRewardAvailabilityContext.Provider value={charterRewardContextValue}>
      <CalendarContainer gap={4}>
        {/* Month Selector - Improved Carousel */}
        <ScrollableContainer gap={0}>
          <div>
            {monthCarousel}
          </div>

          <TripOptionsContainer direction="vertical" gap={0}>
            <MaldivesCharterTripOptionsTable
              currentMonthTripOptions={allDateItems}
              selectedMonth={selectedMonth}
              dateRangesByMonth={dateRangesByMonth}
              luxPlusEnabled={luxPlusEnabled}
              selectedOptionId={selectedOptionId}
              onOptionSelect={handleOptionSelect}
              nights={nights}
              rooms={occupancies || [{ adults: 2, children: 0 }]}
              fareClass={props.selectedFareClass ?? fareClass}
              priceMode={priceMode}
              itemBundleDataByDate={itemBundleDataByDate}
              hotelPriceByCabinAndDate={hotelPriceByCabinAndDate}
              rewardSeatsOnly={rewardSeatsOnly}
              cabinFilter={props.cabinFilter}
            />
            <BottomLinkContainer>
              <BodyText variant="medium" colour="light-foreground-alt">
                <FormattedMessage
                  id="h0D72ZYbNrwG1DVf4SJL"
                  defaultMessage="<strong>Looking for specific dates?</strong> <link>Search all Maldives offers</link>"
                  description="Link at bottom of charter flight calendar to search all Maldives offers"
                  values={{
                    strong: (chunks) => <strong>{chunks}</strong>,
                    link: (chunks) => (
                      <TextLink
                        weight="regular"
                        target="_blank"
                        to={`/search?${buildSearchParamsForFilterPane({
                        originalSearch: '',
                        destinationName: Places.Maldives.name,
                        destinationId: Places.Maldives.id,
                        adults: 2,
                        children: 'none',
                      })}`}
                      >
                        {chunks}
                      </TextLink>
                    ),
                  }}
                />
              </BodyText>
            </BottomLinkContainer>
          </TripOptionsContainer>
        </ScrollableContainer>
      </CalendarContainer>
    </CharterRewardAvailabilityContext.Provider>
  )
}

export default MaldivesDirectSearchFlightCalendar
